import { useEffect, useRef, useState, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import SiteLogo from './SiteLogo.tsx';
import { NeuralNetBackground } from './NeuralNetBackground.tsx';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import '../pages/css/LoginPage.css';
import '../pages/css/RegistrationPage.css';
import './css/Navigation.css';

type Props = {
  title: string;
  lead?: string;
  children: ReactNode;
};

export function AuthSplitPage({ title, lead, children }: Props) {
  const { t, currentLanguage, setLanguage } = useTranslation();
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const year = new Date().getFullYear();
  const languagesToRender = ['ru', 'en'];
  const languageLabel: Record<string, string> = { ru: 'RU', en: 'EN' };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="login-page registration-page">
      <nav className="sciplex-nav login-page__nav">
        <div className="nav-container login-page__nav-container">
          <div className="nav-logo">
            <SiteLogo />
          </div>
          <div className="nav-links-wrap" aria-hidden />
          <div className="language-switcher" ref={langMenuRef}>
            <button
              type="button"
              className="language-select language-select-btn"
              onClick={() => setLangMenuOpen((v) => !v)}
              aria-expanded={langMenuOpen}
              aria-haspopup="listbox"
              aria-label="Выбор языка"
            >
              <span className="language-select-code">
                {languageLabel[currentLanguage] || currentLanguage.toUpperCase()}
              </span>
            </button>
            <div
              className={`language-dropdown ${langMenuOpen ? 'language-dropdown-open' : ''}`}
              role="listbox"
              aria-label="Языки"
            >
              {languagesToRender.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  role="option"
                  aria-selected={currentLanguage === lang}
                  className={`language-option ${currentLanguage === lang ? 'language-option-active' : ''}`}
                  onClick={() => {
                    setLanguage(lang);
                    setLangMenuOpen(false);
                  }}
                >
                  <span>{languageLabel[lang] || lang}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <div className="login-page__body">
        <NeuralNetBackground />
        <div className="login-page__inner">
          <div className="login-page__grid registration-page__grid--single">
            <div className="login-page__panel">
              <div className="login-card">
                <h1 className="login-card__title">{title}</h1>
                {lead ? <p className="login-card__subtitle">{lead}</p> : null}
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="login-page__footer">
        <p className="login-page__copyright">
          © {year} Sciplex. {t('login.footerRights')}
        </p>
        <p className="login-page__legal">
          <Link to="/privacy">{t('login.privacyLink')}</Link>
          <span className="login-page__legal-sep" aria-hidden>
            |
          </span>
          <Link to="/terms">{t('login.termsLink')}</Link>
        </p>
      </footer>
    </div>
  );
}
